/**
 * FR-I6's enquiry form — the seam Wave 3D left inside the `contact` block.
 *
 * 3D's comment said it exactly: *"a form that silently drops an enquiry is
 * worse than no form"*, and until the endpoint existed the block claimed
 * nothing it could not do. It exists now (`app/api/site_forms.py`), so this is
 * the form.
 *
 * ---
 *
 * ## There is no JavaScript in it, and there cannot be
 *
 * ADR-0013 §2 — *no client-side framework runs on a public page* — and the web
 * half turns that into `script-src 'none'` on every public response, so a
 * `<script>` here would not run even if one were written. That is not a
 * limitation this file works around; it is the reason the form is shaped the
 * way it is:
 *
 * * it is a plain `<form method="post">` and the **browser** submits it, so it
 *   works with JavaScript off, on a ten-year-old phone, and behind a proxy that
 *   strips scripts;
 * * `action` comes from the render payload (`payload.forms.enquiry`), because
 *   the path contains the app slug and the slug lives in exactly one setting on
 *   the service side — see `app/domain/site.py: enquiry_action`;
 * * the service answers **303 back to this page** with a fragment, and the
 *   confirmation below is revealed by `:target`. **CSS, not script** — the same
 *   move the preview ribbon makes (`site/render.tsx`).
 *
 * The three notices are always in the markup and always hidden until the
 * fragment names one. That costs about 300 bytes and buys a confirmation that
 * cannot fail, because nothing has to run for it to appear.
 *
 * ## The honeypot
 *
 * One field, hidden three ways — `hidden`, off-screen in CSS, and
 * `aria-hidden` with `tabIndex={-1}` so a screen reader never announces it and
 * a keyboard never lands on it. A person cannot fill it; a bot filling every
 * input will. The service drops those silently and answers exactly as it
 * answers a person, so a bot learns nothing (`app/api/site_forms.py`).
 *
 * `autoComplete="off"` matters here more than it usually does: without it a
 * browser's autofill will happily put a URL in a field called
 * `company_website` and turn a real visitor into a bot.
 */

import type { ReactNode } from 'react'

import { list, obj, str } from '../read'

/** The fragments `app/api/site_forms.py` redirects to. Both halves, one list. */
export const FRAGMENT_SENT = 'enquiry-sent'
export const FRAGMENT_BUSY = 'enquiry-busy'
export const FRAGMENT_FAILED = 'enquiry-failed'

/** The honeypot's field name, matching `domain.HONEYPOT_FIELD` on the service. */
export const HONEYPOT_FIELD = 'company_website'

export interface EnquiryFormProps {
  /** Where the form posts, from `payload.forms.enquiry`. **Non-null**: when the
   *  payload carries no action, `enquiryProps` returns `null` and the block
   *  renders no form at all, rather than a form with nowhere to go. */
  action: string
  /** The page this form is on, sent back so the lead records where it came from
   *  and so the redirect returns here. Validated on the service before it is
   *  echoed into a `Location` — an unvalidated one is an open redirect. */
  pagePath: string
  /** `[{slug, name}]`, resolved from the catalog by the service. */
  services: Array<{ slug: string; name: string }>
  /** Shown in the failure notice, so a visitor whose enquiry we could not take
   *  still has a way to reach the firm. */
  email: string | null
  /**
   * **T-4.28 — because a page may now carry two forms.** FR-I14's `lead_form`
   * is FR-I6's form in a second place, so a `contact` block and a `lead_form`
   * on one page would otherwise emit the same `id` twice and every `<label
   * for>` on the second form would point at the first form's input. The
   * block's index on the page makes each one unique.
   */
  idPrefix: string
  /**
   * Whether *this* form carries the three `:target` notices.
   *
   * There can be exactly one set per page: the service redirects to a fixed
   * fragment (`#enquiry-sent`) and a duplicated id makes `:target` match the
   * first element and leaves the second dead. So `render.tsx` marks the first
   * form-bearing block on the page and only that one draws them; a second form
   * posts to the same endpoint and the visitor is returned to the same
   * confirmation.
   */
  notices: boolean
  /** What the firm typed on a `lead_form`'s `context`, carried onto the lead so
   *  the firm knows which section captured it (FR-H1). Null on a `contact`
   *  block, whose context is the page. */
  context: string | null
  /** The button's words. `lead_form` lets the firm set them; `contact` does
   *  not, and both fall back to the same sentence. */
  submitLabel: string | null
}

/**
 * Read the props of a `contact` block into what this component needs.
 *
 * Tolerant like every other reader here (`site/read.ts`): a payload from a
 * service that does not send `forms` yet, or a `form_services` that arrived as
 * an object, produces a form with no service select or no form at all — never
 * a thrown render on somebody's business address.
 */
export function enquiryProps(
  blockProps: Record<string, unknown>,
  formsAction: string | null,
  pagePath: string,
  email: string | null,
  opts: { idPrefix?: string; notices?: boolean } = {},
): EnquiryFormProps | null {
  // `show_form` defaults to **true** on the service's own block schema
  // (`BLOCK_SCHEMAS['contact']`), so `undefined` means the firm never touched
  // the switch and the form is wanted.
  if (blockProps.show_form === false) return null
  if (!formsAction) return null
  const services = list(blockProps.form_services)
    .map((raw) => {
      const item = obj(raw)
      const slug = str(item.slug)
      const name = str(item.name)
      return slug && name ? { slug, name } : null
    })
    .filter((s): s is { slug: string; name: string } => s !== null)
  return {
    action: formsAction,
    pagePath,
    services,
    email,
    idPrefix: opts.idPrefix ?? 'enq',
    // Defaults to drawing them, which is what a lone `contact` block has always
    // done and what a direct caller (a test, a future surface) should get.
    notices: opts.notices !== false,
    context: str(blockProps.context),
    submitLabel: str(blockProps.submit_label),
  }
}

/**
 * The three `:target` notices, and **exactly one set per page**.
 *
 * The service redirects to a fixed fragment (`#enquiry-sent`), so only one
 * element on the page may carry each id: a second set would be a duplicate id
 * that `:target` never matches, and a confirmation that silently never
 * appears. `render.tsx` marks the first form-bearing block on the page and
 * only that one draws these; a second form posts to the same endpoint and the
 * visitor lands on the same message (T-4.28).
 *
 * `tabIndex` so a keyboard follow of the fragment lands on the message rather
 * than past it.
 */
function EnquiryNotices({ email }: { email: string | null }): ReactNode {
  const orWriteTo = email ? (
    <>
      {' '}
      or write to <a href={`mailto:${email}`}>{email}</a>
    </>
  ) : null
  return (
    <>
      <p className="enquiry-notice enquiry-sent" id={FRAGMENT_SENT} tabIndex={-1} role="status">
        <strong>Thank you — we have your enquiry.</strong> Someone from the firm will be in touch.
        If it is urgent, please telephone or write to us directly.
      </p>
      <p className="enquiry-notice enquiry-busy" id={FRAGMENT_BUSY} tabIndex={-1} role="status">
        <strong>We could not take that just now.</strong> A few too many enquiries have come from
        your connection in the last little while. Please try again shortly{orWriteTo}.
      </p>
      <p className="enquiry-notice enquiry-failed" id={FRAGMENT_FAILED} tabIndex={-1} role="status">
        <strong>That did not go through.</strong> Nothing was recorded, so please try
        again{orWriteTo}.
      </p>
    </>
  )
}

export function EnquiryForm(props: EnquiryFormProps): ReactNode {
  const { action, pagePath, services, email, idPrefix, notices, context, submitLabel } = props
  const id = (name: string) => `${idPrefix}-${name}`
  return (
    <div className="enquiry">
      {notices ? <EnquiryNotices email={email} /> : null}

      <form className="enquiry-form" method="post" action={action}>
        {/* Where this was submitted from. The service validates it as a
            same-origin path before echoing it into the redirect. */}
        <input type="hidden" name="page" value={pagePath} />
        {/* FR-I14 / T-4.28: which section captured this, when the firm named
            one. It rides onto the lead beside the page, so two forms on one
            page are told apart afterwards. Absent on a `contact` block — the
            page is its context. */}
        {context ? <input type="hidden" name="context" value={context} /> : null}

        <div className="field">
          <label htmlFor={id('name')}>Your name</label>
          <input id={id('name')} name="full_name" type="text" maxLength={200} required autoComplete="name" />
        </div>

        {/* Neither is `required` on its own: the service asks for **one** of
            them, and marking both required would refuse an enquiry from
            somebody who only wants to be telephoned. The hint says so. */}
        <div className="field-pair">
          <div className="field">
            <label htmlFor={id('email')}>Email</label>
            <input id={id('email')} name="email" type="email" maxLength={320} autoComplete="email" />
          </div>
          <div className="field">
            <label htmlFor={id('mobile')}>Telephone</label>
            <input id={id('mobile')} name="mobile" type="tel" maxLength={20} autoComplete="tel" />
          </div>
        </div>
        <p className="field-hint">Please leave at least one of these so we can reply.</p>

        <div className="field">
          <label htmlFor={id('org')}>Your business (if any)</label>
          <input
            id={id('org')}
            name="organisation_name"
            type="text"
            maxLength={200}
            autoComplete="organization"
          />
        </div>

        {services.length > 0 ? (
          <div className="field">
            <label htmlFor={id('service')}>What is this about?</label>
            <select id={id('service')} name="service" defaultValue="">
              <option value="">Something else</option>
              {services.map((service) => (
                <option key={service.slug} value={service.slug}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="field">
          <label htmlFor={id('message')}>How can we help?</label>
          <textarea id={id('message')} name="message" rows={4} maxLength={4000} />
        </div>

        {/* The honeypot. Never announced, never focusable, never autofilled. */}
        <div className="enquiry-trap" aria-hidden="true">
          <label htmlFor={id('trap')}>Do not fill this in</label>
          <input
            id={id('trap')}
            name={HONEYPOT_FIELD}
            type="text"
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        <p className="enquiry-actions">
          <button className="button primary" type="submit">
            {submitLabel ?? 'Send enquiry'}
          </button>
        </p>
        {/* No consent checkbox and no privacy claim invented here. What a firm
            says about the data it collects is the firm's `privacy` page
            (FR-I2), and putting words in its mouth on a regulated
            professional's website is not this renderer's to do. */}
      </form>
    </div>
  )
}

/**
 * The form's stylesheet, appended by `site/render.tsx` after the template's.
 *
 * It lives here rather than in `templates/base.ts` for one reason worth
 * stating: the `:target` rules **are** the confirmation mechanism, so the CSS
 * and the markup that depend on each other are in one file and move together.
 * Everything else uses the palette's own custom properties, so a firm's colours
 * apply to the form without a template knowing it exists.
 *
 * The 44px minimum on controls is not decoration — Wave 3D's mobile audit
 * (`tests/host-switch.spec.ts`) fails a control shorter than 24px, and it found
 * two real ones on this site's nav and cards. A text input a finger cannot hit
 * is the same defect one door along.
 */
export const ENQUIRY_CSS =
  '.enquiry{margin-top:1.5rem}' +
  '.enquiry-form{display:grid;gap:.9rem;max-width:34rem}' +
  '.enquiry-form .field{display:grid;gap:.3rem}' +
  '.enquiry-form label{font-size:.92rem;font-weight:600;color:var(--ink)}' +
  '.enquiry-form input,.enquiry-form select,.enquiry-form textarea{' +
  'font:inherit;font-size:1rem;min-height:44px;padding:.6rem .75rem;color:var(--ink);' +
  'background:var(--paper);border:1px solid var(--rule);border-radius:.4rem;width:100%}' +
  '.enquiry-form textarea{min-height:7rem;resize:vertical}' +
  '.enquiry-form input:focus-visible,.enquiry-form select:focus-visible,' +
  '.enquiry-form textarea:focus-visible{border-color:var(--primary)}' +
  '.field-pair{display:grid;gap:.9rem;grid-template-columns:1fr}' +
  '.field-hint{margin:-.35rem 0 0;font-size:.88rem;color:var(--ink-soft)}' +
  '.enquiry-actions{margin:.4rem 0 0}' +
  '.enquiry-form button{cursor:pointer;min-height:44px}' +
  // The honeypot: out of the layout, out of the tab order, out of the
  // accessibility tree. Not `display:none` — some bots skip those.
  '.enquiry-trap{position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden}' +
  // The confirmation, revealed by the fragment the service redirects to.
  '.enquiry-notice{display:none;margin:0 0 1rem;padding:.85rem 1rem;border-radius:.4rem;' +
  'border:1px solid var(--rule);background:var(--card);font-size:.97rem}' +
  '.enquiry-notice:target{display:block}' +
  '.enquiry-sent:target{border-color:var(--primary);background:var(--primary-tint)}' +
  '.enquiry-notice:focus{outline:none}' +
  '@media (min-width:40rem){.field-pair{grid-template-columns:1fr 1fr}}'
